import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  Divider,
  CircularProgress,
  IconButton,
  Button,
  TextField,
  Avatar,
} from '@mui/material';
import { ArrowBack, Edit, Save, Cancel, Add, DeleteOutline, Print } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format } from 'date-fns';
import { useReactToPrint } from 'react-to-print';
import api from '../../utils/axios';
import Toast from '../../components/common/Toast';

const taskStatusColors = {
  not_started: 'default',
  in_progress: 'warning',
  not_achieved: 'error',
  achieved: 'success'
};

const taskStatusLabels = {
  not_started: 'Not Started',
  in_progress: 'In Progress',
  not_achieved: 'Not Achieved',
  achieved: 'Achieved'
};

const priorityColors = {
  critical: 'error',
  high: 'warning',
  medium: 'primary',
  low: 'default',
};

const priorityLabels = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
};

const KPIDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useSelector(state => state.auth);
  const isCoach = user?.role === 'coach' || user?.role === 'superAdmin';

  const [kpi, setKpi] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const printRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: kpi?.name || 'KPI Document',
    pageStyle: `
      @page {
        margin: 15mm 20mm;
        @top-center {
          content: "${(kpi?.name || 'KPI Document').replace(/"/g, '\\"')}";
          font-size: 14pt;
          font-weight: bold;
          color: #222;
          font-family: sans-serif;
        }
        @bottom-center {
          content: "Page " counter(page);
          font-size: 9pt;
          color: #555;
          font-family: sans-serif;
        }
      }
      body { padding: 0; }
      .no-print { display: none !important; }
      .task-status-print, .task-priority-print {
        display: inline-block !important;
        font-size: 9pt; font-weight: 600;
        padding: 1pt 5pt; border-radius: 3pt;
        border: 1pt solid #999; margin-left: 6pt; color: #333;
      }
      .MuiFormControl-root { display: none !important; }
      .kpi-metric-section {
        break-inside: avoid;
        border: 1pt solid #ccc;
        border-radius: 4pt; padding: 10pt; margin-bottom: 12pt;
      }
    `,
  });

  useEffect(() => {
    fetchKPI();
  }, [id]);

  const handleCloseToast = () => setToast(prev => ({ ...prev, open: false }));
  const showToast = (message, severity = 'success') => setToast({ open: true, message, severity });

  const fetchKPI = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/kpi/${id}`);
      setKpi(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch KPI');
      showToast(err.response?.data?.message || 'Failed to fetch KPI', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (areaId, taskId, newStatus) => {
    try {
      setError(null);
      await api.patch(`/kpi/${id}/areas/${areaId}/tasks/${taskId}`, { status: newStatus });
      setKpi(prevKpi => ({
        ...prevKpi,
        areas: prevKpi.areas.map(area => {
          if (area._id === areaId) {
            return {
              ...area,
              tasks: area.tasks.map(task =>
                task._id === taskId ? { ...task, status: newStatus } : task
              )
            };
          }
          return area;
        })
      }));
      showToast('Task status updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update task status');
      showToast(err.response?.data?.message || 'Failed to update task status', 'error');
    }
  };

  const handlePriorityChange = async (areaId, taskId, newPriority) => {
    try {
      setError(null);
      await api.patch(`/kpi/${id}/areas/${areaId}/tasks/${taskId}`, { priority: newPriority });
      setKpi(prevKpi => ({
        ...prevKpi,
        areas: prevKpi.areas.map(area => {
          if (area._id === areaId) {
            return {
              ...area,
              tasks: area.tasks.map(task =>
                task._id === taskId ? { ...task, priority: newPriority } : task
              )
            };
          }
          return area;
        })
      }));
      showToast('Task priority updated successfully');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update task priority');
      showToast(err.response?.data?.message || 'Failed to update task priority', 'error');
    }
  };

  const startEdit = () => {
    setEditData(JSON.parse(JSON.stringify(kpi)));
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditData(null);
  };

  const handleSave = async () => {
    try {
      const response = await api.put(`/kpi/${id}`, {
        name: editData.name,
        reviewDate: editData.reviewDate,
        areas: editData.areas,
      });
      setKpi(response.data);
      setEditMode(false);
      setEditData(null);
      showToast('KPI updated successfully');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update KPI', 'error');
    }
  };

  // Edit helpers
  const updateArea = (areaIdx, field, value) => {
    const areas = [...editData.areas];
    areas[areaIdx] = { ...areas[areaIdx], [field]: value };
    setEditData({ ...editData, areas });
  };

  const addArea = () => {
    setEditData({
      ...editData,
      areas: [...editData.areas, { name: '', description: '', targetDate: null, tasks: [] }]
    });
  };

  const removeArea = (areaIdx) => {
    setEditData({ ...editData, areas: editData.areas.filter((_, i) => i !== areaIdx) });
  };

  const updateTask = (areaIdx, taskIdx, field, value) => {
    const areas = [...editData.areas];
    const tasks = [...areas[areaIdx].tasks];
    tasks[taskIdx] = { ...tasks[taskIdx], [field]: value };
    areas[areaIdx] = { ...areas[areaIdx], tasks };
    setEditData({ ...editData, areas });
  };

  const addTask = (areaIdx) => {
    const areas = [...editData.areas];
    areas[areaIdx] = {
      ...areas[areaIdx],
      tasks: [...areas[areaIdx].tasks, { name: '', status: 'not_started', priority: 'low' }]
    };
    setEditData({ ...editData, areas });
  };

  const removeTask = (areaIdx, taskIdx) => {
    const areas = [...editData.areas];
    areas[areaIdx] = {
      ...areas[areaIdx],
      tasks: areas[areaIdx].tasks.filter((_, i) => i !== taskIdx)
    };
    setEditData({ ...editData, areas });
  };

  const calculateAreaProgress = (tasks) => {
    if (!tasks.length) return 0;
    return Math.round((tasks.filter(t => t.status === 'achieved').length / tasks.length) * 100);
  };

  const calculateKPIProgress = (areas) => {
    const total = areas.reduce((sum, a) => sum + a.tasks.length, 0);
    if (!total) return 0;
    const achieved = areas.reduce((sum, a) => sum + a.tasks.filter(t => t.status === 'achieved').length, 0);
    return Math.round((achieved / total) * 100);
  };

  if (loading) {
    return (
      <Container>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!kpi) {
    return (
      <Container>
        <Alert severity="error">KPI not found</Alert>
      </Container>
    );
  }

  const createdById = kpi.createdBy?._id?.toString() || kpi.createdBy?.toString();
  const currentUserId = user?.id?.toString() || user?._id?.toString();
  const canEdit = isCoach && createdById === currentUserId;

  return (
    <Container>
      <Toast open={toast.open} message={toast.message} severity={toast.severity} onClose={handleCloseToast} />

      <Paper className="kpi-print-content" ref={printRef} sx={{ p: 3, mt: 3 }}>
        {!kpi.isTemplate && kpi.companyId && (
          <Box className="kpi-print-header" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 3, gap: 1 }}>
            {kpi.companyId.logo && (
              <Avatar
                src={kpi.companyId.logo}
                variant="rounded"
                sx={{ width: 160, height: 'auto', '& img': { objectFit: 'contain', height: 'auto' } }}
              />
            )}
            {kpi.companyId.name && (
              <Typography variant="subtitle1" fontWeight={600} color="text.secondary">
                {kpi.companyId.name}
              </Typography>
            )}
          </Box>
        )}
        <Box className="no-print" sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton onClick={() => navigate('/my-kpis')} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <Typography variant="h4" sx={{ flex: 1 }}>
            {editMode ? 'Edit KPI' : (kpi.name || 'KPI Details')}
          </Typography>
          {!editMode && (
            <Button variant="outlined" startIcon={<Print />} onClick={handlePrint} sx={{ mr: 1 }}>
              Print
            </Button>
          )}
          {canEdit && !editMode && (
            <Button variant="outlined" startIcon={<Edit />} onClick={startEdit}>
              Edit
            </Button>
          )}
          {editMode && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" startIcon={<Save />} onClick={handleSave}>
                Save
              </Button>
              <Button variant="outlined" startIcon={<Cancel />} onClick={cancelEdit}>
                Cancel
              </Button>
            </Box>
          )}
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {/* Review date */}
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ flex: 1 }}>
            {editMode ? (
              <Box sx={{ display: 'flex', gap: 2, mb: 1, alignItems: 'center' }}>
                <TextField
                  label="KPI Name"
                  value={editData.name || ''}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  size="medium"
                  required
                  sx={{ flex: 1 }}
                />
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    label="Review Date"
                    value={editData.reviewDate ? new Date(editData.reviewDate) : null}
                    onChange={(date) => setEditData({ ...editData, reviewDate: date })}
                    renderInput={(params) => <TextField {...params} size="small" sx={{ width: '25%' }} />}
                  />
                </LocalizationProvider>
              </Box>
            ) : (
              <>
                {!kpi.isTemplate && kpi.userId && (
                  <Typography variant="body1" className="kpi-print-meta" sx={{ mb: 0.5 }}>
                    Assigned to: <strong>{kpi.userId.name || kpi.userId}</strong>
                  </Typography>
                )}
                {!kpi.isTemplate && kpi.userId?.reportsTo && (
                  <Typography variant="body1" className="kpi-print-meta" sx={{ mb: 0.5 }}>
                    Reporting to: <strong>{kpi.userId.reportsTo.name}</strong>
                  </Typography>
                )}
                <Typography variant="h6" className="kpi-print-meta" gutterBottom>
                  Review Date: {kpi.reviewDate ? format(new Date(kpi.reviewDate), 'PPP') : 'Not set'}
                </Typography>
              </>
            )}
            <Typography variant="body2" className="kpi-print-meta" color="text.secondary">
              Created: {format(new Date(kpi.createdAt), 'PPP')}
            </Typography>
          </Box>
          {!editMode && (
            <Chip
              label={`Overall Progress: ${calculateKPIProgress(kpi.areas)}%`}
              color={calculateKPIProgress(kpi.areas) === 100 ? 'success' : 'primary'}
              size="large"
              sx={{ fontSize: '1rem', height: 40, px: 2 }}
            />
          )}
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* View mode */}
        {!editMode && kpi.areas.map((area) => (
          <Box key={area._id} className="kpi-metric-section" sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" className="metric-title" sx={{ fontSize: '1.5rem', fontWeight: 700 }}>{area.name}</Typography>
              <Chip
                label={`${calculateAreaProgress(area.tasks)}% Complete`}
                color={calculateAreaProgress(area.tasks) === 100 ? 'success' : 'primary'}
              />
            </Box>
            {area.description && (
              <Box className="metric-comment" sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, borderLeft: '3px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  Comment
                </Typography>
                <Typography variant="body2">{area.description}</Typography>
              </Box>
            )}
            <Grid container spacing={2}>
              {area.tasks.map((task) => (
                <Grid item xs={12} key={task._id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'nowrap', overflow: 'hidden' }}>
                        <Typography variant="body1" sx={{ flex: 1, minWidth: 0 }}>{task.name}</Typography>
                        {/* Print-only plain text labels */}
                        <span className="task-priority-print" style={{ display: 'none' }}>{priorityLabels[task.priority || 'low']}</span>
                        <span className="task-status-print" style={{ display: 'none' }}>{taskStatusLabels[task.status]}</span>
                        {/* Screen-only interactive selects */}
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <Select
                            value={task.priority || 'low'}
                            onChange={(e) => handlePriorityChange(area._id, task._id, e.target.value)}
                          >
                            {Object.entries(priorityLabels).map(([value, label]) => (
                              <MenuItem key={value} value={value}>
                                <Chip label={label} color={priorityColors[value]} size="small" sx={{ minWidth: 70 }} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                          <Select
                            value={task.status}
                            onChange={(e) => handleStatusChange(area._id, task._id, e.target.value)}
                          >
                            {Object.entries(taskStatusLabels).map(([value, label]) => (
                              <MenuItem key={value} value={value}>
                                <Chip label={label} color={taskStatusColors[value]} size="small" sx={{ minWidth: 100 }} />
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        ))}

        {/* Edit mode */}
        {editMode && (
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            {editData.areas.map((area, areaIdx) => (
              <Paper key={areaIdx} variant="outlined" sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <TextField
                    label="KPI Metric"
                    value={area.name}
                    onChange={(e) => updateArea(areaIdx, 'name', e.target.value)}
                    size="medium"
                    sx={{ flex: 1 }}
                  />
                  <IconButton color="error" onClick={() => removeArea(areaIdx)}>
                    <DeleteOutline />
                  </IconButton>
                </Box>
                <TextField
                  label="Comment"
                  value={area.description || ''}
                  onChange={(e) => updateArea(areaIdx, 'description', e.target.value)}
                  multiline
                  rows={2}
                  fullWidth
                  size="medium"
                  sx={{ mb: 2 }}
                />
                <Grid container spacing={2}>
                  {area.tasks.map((task, taskIdx) => (
                    <Grid item xs={12} md={6} key={taskIdx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <TextField
                          label="Task"
                          value={task.name}
                          onChange={(e) => updateTask(areaIdx, taskIdx, 'name', e.target.value)}
                          size="small"
                          sx={{ flex: 1 }}
                        />
                        <FormControl size="small" sx={{ minWidth: 110 }}>
                          <InputLabel>Priority</InputLabel>
                          <Select
                            label="Priority"
                            value={task.priority || 'low'}
                            onChange={(e) => updateTask(areaIdx, taskIdx, 'priority', e.target.value)}
                          >
                            {Object.entries(priorityLabels).map(([value, label]) => (
                              <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 140 }}>
                          <Select
                            value={task.status}
                            onChange={(e) => updateTask(areaIdx, taskIdx, 'status', e.target.value)}
                          >
                            {Object.entries(taskStatusLabels).map(([value, label]) => (
                              <MenuItem key={value} value={value}>{label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <IconButton size="small" color="error" onClick={() => removeTask(areaIdx, taskIdx)}>
                          <DeleteOutline fontSize="small" />
                        </IconButton>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
                <Button
                  size="small"
                  startIcon={<Add />}
                  onClick={() => addTask(areaIdx)}
                  sx={{ mt: 1 }}
                >
                  Add Task
                </Button>
              </Paper>
            ))}
            <Button variant="outlined" startIcon={<Add />} onClick={addArea}>
              Add Area
            </Button>
          </LocalizationProvider>
        )}
      </Paper>
    </Container>
  );
};

export default KPIDetailsPage;
